import { PhotoCheckConfig, PhotoCheckRect } from '@/types/photo-check.type';

// Jev accepts JPEG or PNG, 280 to 1568 px a side, and does not resize or rotate. The browser does both before upload.
const MAX_SIDE = 1568;
const MIN_SIDE = 280;
// Photos at least this big are also sent as overlapping parts, so small things are not lost when shrunk to 1568 px
const SPLIT_FROM_PX = 2000;
const WHOLE: PhotoCheckRect = { top: 0, left: 0, bottom: 1, right: 1 };

// Video: Jev reads images only, so a clip is checked as frames
export const VIDEO_MAX_SECONDS = 20;
export const VIDEO_MAX_BYTES = 50 * 1024 * 1024;
const VIDEO_FRAME_EVERY_SECONDS = 2;
const VIDEO_MAX_FRAMES = 8;

export interface PhotoCheckView {
  blob: Blob;
  rect: PhotoCheckRect;
  // For showing the photo or frame with boxes on it
  url: string;
}

export interface PreparedMedia {
  media: 'photo' | 'video';
  views: PhotoCheckView[];
}

// <img> applies EXIF rotation in current mobile browsers and decodes HEIC on iOS
function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(
        new Error(
          'This photo could not be read. Try a JPEG, or set iPhone Camera > Formats > Most Compatible.'
        )
      );
    img.src = URL.createObjectURL(file);
  });
}

// One part of an image or video frame (rect in fractions of the whole) as a JPEG within Jev's limits
function toJpeg(
  source: CanvasImageSource,
  width: number,
  height: number,
  rect: PhotoCheckRect
): Promise<Blob> {
  const sw = (rect.right - rect.left) * width;
  const sh = (rect.bottom - rect.top) * height;
  let scale = Math.min(1, MAX_SIDE / Math.max(sw, sh));
  scale = Math.max(scale, MIN_SIDE / Math.min(sw, sh));

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(sw * scale);
  canvas.height = Math.round(sh * scale);
  canvas
    .getContext('2d')
    ?.drawImage(
      source,
      rect.left * width,
      rect.top * height,
      sw,
      sh,
      0,
      0,
      canvas.width,
      canvas.height
    );

  return new Promise((resolve, reject) =>
    canvas.toBlob(
      blob =>
        blob
          ? resolve(blob)
          : reject(new Error('Could not prepare the photo.')),
      'image/jpeg',
      0.85
    )
  );
}

// 2x2 overlapping parts, 3 across for very wide photos and 3 down for very tall ones (same grid as the tested prototype)
export function partRects(
  width: number,
  height: number,
  overlap = 0.15
): PhotoCheckRect[] {
  const aspect = width / height;
  const cols = aspect >= 1.6 ? 3 : 2;
  const rows = aspect <= 1 / 1.6 ? 3 : 2;
  const w = (1 + overlap * (cols - 1)) / cols;
  const h = (1 + overlap * (rows - 1)) / rows;
  const rects: PhotoCheckRect[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const left = c * (w - overlap);
      const top = r * (h - overlap);
      rects.push({
        top,
        left,
        bottom: Math.min(1, top + h),
        right: Math.min(1, left + w)
      });
    }
  }
  return rects;
}

/**
 * A creator's example photo as a JPEG within Jev's limits, so the server can compare run photos with it.
 * A photo the browser cannot read is kept as it is: it is still shown to people, just not compared.
 */
export async function prepareExamplePhoto(file: File): Promise<File> {
  try {
    const img = await loadImage(file);
    const blob = await toJpeg(img, img.naturalWidth, img.naturalHeight, WHOLE);
    URL.revokeObjectURL(img.src);
    const name = file.name.replace(/\.[^.]*$/, '') || 'example';
    return new File([blob], name + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

// The whole photo first, then its parts when the photo is big enough that the parts add detail
export async function preparePhoto(file: Blob): Promise<PreparedMedia> {
  const img = await loadImage(file);
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  const rects =
    Math.max(width, height) >= SPLIT_FROM_PX
      ? [WHOLE, ...partRects(width, height)]
      : [WHOLE];

  const views: PhotoCheckView[] = [];
  for (const rect of rects) {
    const blob = await toJpeg(img, width, height, rect);
    views.push({ blob, rect, url: URL.createObjectURL(blob) });
  }
  return { media: 'photo', views };
}

function waitFor(video: HTMLVideoElement, event: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const done = () => {
      video.removeEventListener(event, done);
      resolve();
    };
    video.addEventListener(event, done);
    video.addEventListener(
      'error',
      () =>
        reject(new Error('This video could not be read. Try an MP4 or MOV.')),
      { once: true }
    );
  });
}

// About one frame every 2 seconds, at most 8. iOS Safari needs muted + playsInline to decode without full screen.
export async function prepareVideo(file: Blob): Promise<PreparedMedia> {
  if (file.size > VIDEO_MAX_BYTES) {
    throw new Error(
      `Video is larger than ${
        VIDEO_MAX_BYTES / 1024 / 1024
      } MB. Record a shorter clip.`
    );
  }

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = URL.createObjectURL(file);
  await waitFor(video, 'loadedmetadata');

  const duration = video.duration;
  if (!isFinite(duration) || duration <= 0) {
    throw new Error('This video could not be read. Try an MP4 or MOV.');
  }
  if (duration > VIDEO_MAX_SECONDS + 1) {
    throw new Error(
      `Video is longer than ${VIDEO_MAX_SECONDS} seconds. Record a shorter clip.`
    );
  }

  const count = Math.min(
    VIDEO_MAX_FRAMES,
    Math.max(1, Math.floor(duration / VIDEO_FRAME_EVERY_SECONDS))
  );
  const views: PhotoCheckView[] = [];
  for (let i = 0; i < count; i++) {
    // Middle of each slot, so the first frame is not a blurry start
    video.currentTime = Math.min(
      duration - 0.05,
      ((i + 0.5) * duration) / count
    );
    await waitFor(video, 'seeked');
    const blob = await toJpeg(
      video,
      video.videoWidth,
      video.videoHeight,
      WHOLE
    );
    views.push({ blob, rect: WHOLE, url: URL.createObjectURL(blob) });
  }

  URL.revokeObjectURL(video.src);
  return { media: 'video', views };
}

export function prepareMedia(file: File): Promise<PreparedMedia> {
  return file.type.startsWith('video/')
    ? prepareVideo(file)
    : preparePhoto(file);
}

export interface PhotoCheckTaskInput {
  // The published task id (run_checklist.task_id)
  id: string;
  title?: string;
  photo_check: PhotoCheckConfig;
}

// The multipart body for POST /run/photo-check and /public/run/photo-check
export function buildPhotoCheckForm(
  prepared: PreparedMedia,
  request: {
    area?: string;
    tasks: PhotoCheckTaskInput[];
    run_id?: string;
    checklist_id?: string;
    section_id?: string;
    video_key?: string;
    taken_at?: string;
    looks_like?: string;
    location?: { lat: number; lng: number; accuracy: number };
  }
) {
  const form = new FormData();
  prepared.views.forEach((view, i) =>
    form.append('views', view.blob, `view-${i}.jpg`)
  );
  form.append(
    'request',
    JSON.stringify({
      ...request,
      media: prepared.media,
      rects: prepared.views.map(view => view.rect)
    })
  );
  return form;
}
